import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDrop } from 'react-dnd';
import { v4 as uuidv4 } from 'uuid';
import { Icon } from '@iconify/react';
import { Button, Grid, Collapse } from '@mui/material';
import WorkoutModal from './WorkoutModal';

const DroppableContainer = ({ onItemAdded, getDroppedItems }) => {
  const [droppedItems, setDroppedItems] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentWorkout, setCurrentWorkout] = useState(null);
  const [expanded, setExpanded] = useState({});

  const getItems = () => {
    return droppedItems; // Exposes droppedItems to parent
  };

  useEffect(() => {
    if (getDroppedItems) {
      getDroppedItems(getItems); // Sends the function to parent if provided
    }
  }, [droppedItems, getDroppedItems]);

  const [, drop] = useDrop(() => ({
    accept: 'WORKOUT',
    drop: ({ workout }) => {
      const newWorkout = { 
        ...workout, 
        id: uuidv4(),
        oldId: workout.id, 
        workout_order: droppedItems.length,
        cardioValues: { calories: 0, time_minutes: 0, distance_km: 0 },
        strengthValues: { time_minutes: 0, sets: 0, repetitions: 0 }
      };
      setDroppedItems((prev) => {
        const newWorkout = { 
          ...workout, 
          id: uuidv4(),
          oldId: workout.id, 
          workout_order: prev.length + 1,
          cardioValues: { calories: 0, time_minutes: 0, distance_km: 0 },
          strengthValues: { time_minutes: 0, sets: 0, repetitions: 0 }
        };
        return [...prev, newWorkout];
      });
      onItemAdded(newWorkout);
    },
  }));

  const openModal = (workout) => {
    setCurrentWorkout(workout);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentWorkout(null);
  };

  const handleSaveWorkout = (updatedWorkout) => {
    setDroppedItems((prev) =>
      prev.map((workout) => (workout.id === updatedWorkout.id ? updatedWorkout : workout))
    );
    onItemAdded(updatedWorkout);
    closeModal();
  };

  const handleExpandClick = (id) => {
    setExpanded((prevExpanded) => ({
      ...prevExpanded,
      [id]: !prevExpanded[id],
    }));
  };

  const handleMoveItem = (id, direction) => {
    const index = droppedItems.findIndex((item) => item.id === id);
    
    if (index === -1) return;
  
    const newOrder = [...droppedItems];
    
    if (direction === 'up' && index > 0) {
      const item = newOrder[index];
      const previousItem = newOrder[index - 1];
  
      [newOrder[index], newOrder[index - 1]] = [previousItem, item];
  
      item.workout_order = previousItem.workout_order;
      previousItem.workout_order = item.workout_order + 1;
    } else if (direction === 'down' && index < droppedItems.length - 1) {
      const item = newOrder[index];
      const nextItem = newOrder[index + 1];
  
      [newOrder[index], newOrder[index + 1]] = [nextItem, item];
  
      item.workout_order = nextItem.workout_order;
      nextItem.workout_order = item.workout_order - 1;
    }
  
    setDroppedItems(newOrder);
  };

  const handleRemoveItem = (id) => {
    const updatedItems = droppedItems.filter((item) => item.id !== id);
    const removedIndex = droppedItems.findIndex((item) => item.id === id);

    const reorderedItems = updatedItems.map((item, index) => {
      if (index >= removedIndex) {
        return {
          ...item,
          workout_order: item.workout_order - 1,
        };
      }
      return item;
    });

    setDroppedItems(reorderedItems);
  };

  const isWorkoutEdited = (workout) => {
    return (
      workout.cardioValues.time_minutes > 0 ||
      workout.cardioValues.distance_km > 0 ||
      workout.cardioValues.calories > 0 ||
      workout.strengthValues.time_minutes > 0 ||
      workout.strengthValues.sets > 0 ||
      workout.strengthValues.repetitions > 0
    );
  };

  return (
    <div style={{height: '100%'}}>
      <div
        ref={drop}
        style={{
          border: '2px dashed gray',
          padding: '20px',
          height: '100%',
          boxSizing: 'border-box',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {droppedItems.map((workout) => (
          <div key={workout.id} style={{ margin: '5px', padding: '5px', border: '1px solid #ccc' }}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs>
                <div>{workout.name}</div>
              </Grid>
              <Grid item>
                <Button
                  onClick={() => handleRemoveItem(workout.id)}
                  style={{
                    backgroundColor: 'red',
                    color: '#fff',
                    margin: '1px',
                  }}
                >
                  <Icon icon="pajamas:remove" width="24" height="24" />
                </Button>
                <Button
                  onClick={() => handleMoveItem(workout.id, 'up')}
                  style={{
                    backgroundColor: '#007bff',
                    color: '#fff',
                    margin: '1px',
                  }}
                >
                  <Icon icon="bxs:hand-up" width="24" height="24" />
                </Button>
                <Button
                  onClick={() => handleMoveItem(workout.id, 'down')}
                  style={{
                    backgroundColor: '#007bff',
                    color: '#fff',
                    margin: '1px',
                  }}
                >
                  <Icon icon="bxs:hand-down" width="24" height="24" />
                </Button>
                <Button
                  onClick={() => openModal(workout)}
                  style={{
                    margin: '1px',
                    backgroundColor: 'green',
                    color: '#fff',
                    borderRadius: '4px',
                    padding: '5px 10px',
                  }}
                >
                  <Icon icon="mdi:pencil"
                    width="24"
                    height="24" 
                  />
                </Button>
                <Button
                  onClick={() => handleExpandClick(workout.id)}
                  style={{ backgroundColor: '#007bff', color: '#fff', margin: '1px' }}
                >
                  <Icon
                    icon={expanded[workout.id] ? 'ic:baseline-expand-less' : 'ic:baseline-expand-more'}
                    width="24"
                    height="24"
                  />
                </Button>
                <Icon
                  icon={isWorkoutEdited(workout) ? 'material-symbols:check-circle' : 'material-symbols:check-circle'}
                  style={{ marginLeft: '10px', width: '24', height: '24', color: isWorkoutEdited(workout) ? 'green' : 'gray' }}
                />
              </Grid>
            </Grid>

            <Collapse in={expanded[workout.id]}>
              <div>
                <p>{workout.description}</p>
                <div>
                  <h4>Redoslijed</h4>
                  <p>Redoslijed vježbe: {workout.workout_order}</p>
                  <h4>Kardio detalji</h4>
                  <p>Kalorije: {workout.cardioValues.calories}</p>
                  <p>Vrijeme (minute): {workout.cardioValues.time_minutes}</p>
                  <p>Udaljenost (km): {workout.cardioValues.distance_km}</p>

                  <h4>Snaga Detalji</h4>
                  <p>Vrijeme (minutes): {workout.strengthValues.time_minutes}</p>
                  <p>Setovi: {workout.strengthValues.sets}</p>
                  <p>Ponavljanja: {workout.strengthValues.repetitions}</p>
                </div>
              </div>
            </Collapse>
          </div>
        ))}
      </div>

      {/* Render Workout Modal */}
      <WorkoutModal
        isOpen={isModalOpen}
        workout={currentWorkout}
        onClose={closeModal}
        onSave={handleSaveWorkout}
      />
    </div>
  );
};

DroppableContainer.propTypes = {
  onItemAdded: PropTypes.func.isRequired,
  getDroppedItems: PropTypes.func
};

export default DroppableContainer;
